<template>
  <div class="cs-root" ref="container">
    <!-- Trigger: display + browse icon -->
    <div :class="['cs-trigger', { 'cs-trigger--compact': compact }]" ref="trigger" @click="openBrowse">
      <div class="cs-display" ref="display">
        <template v-if="loading || (!loaded && modelValue?.length)">
          <div class="cs-loading-chips">
            <span class="cs-chip-skeleton skeleton-pulse" />
            <span class="cs-chip-skeleton cs-chip-skeleton--sm skeleton-pulse" />
          </div>
        </template>
        <template v-else-if="selectedCategories.length">
          <span v-for="cat in visibleChips" :key="cat._id" class="cs-chip">
            {{ cat.name }}
            <span @click.stop="remove(cat._id)" class="cs-chip-x">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </span>
          </span>
          <span v-if="hiddenCount > 0" class="cs-count">+{{ hiddenCount }}</span>
        </template>
        <span v-else class="cs-placeholder">{{ placeholder }}</span>
      </div>
      <svg class="cs-browse-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg>
    </div>
    <!-- Browse Modal -->
    <Teleport to="body">
      <Transition name="cs-dd">
        <div v-if="showBrowse" class="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div class="absolute inset-0 bg-black/30 backdrop-blur-sm" />
          <div class="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col animate-fade-in" @click.stop>
            <div class="flex items-center justify-between px-6 pt-5 pb-3 shrink-0">
              <h3 class="text-lg font-semibold text-gray-900">Browse Categories</h3>
              <button @click="cancelBrowse" class="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <!-- Search -->
            <div class="px-6 pb-3 shrink-0">
              <div class="relative">
                <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <input v-model="browseQuery" type="text" placeholder="Search categories..." class="w-full h-10 pl-9 pr-3 rounded-lg border border-gray-200 text-sm outline-none focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary-glow)]" />
              </div>
            </div>
            <!-- Tree -->
            <div class="flex-1 overflow-y-auto px-4 pb-2">
              <div v-if="loading" class="py-10 text-center"><div class="cs-spinner mx-auto" /></div>
              <div v-else-if="!browseVisibleItems.length" class="py-10 text-center text-sm text-gray-400">No categories found</div>
              <div v-else class="space-y-0.5">
                <div
                  v-for="item in browseVisibleItems" :key="item._id"
                  @click="toggleBrowse(item)"
                  :class="['flex items-center py-2 px-2 rounded-lg cursor-pointer transition-colors', isBrowseSelected(item._id) ? 'bg-[var(--color-primary-light)]' : 'hover:bg-gray-50']"
                >
                  <span :style="{ width: item._depth * 20 + 'px' }" class="shrink-0" />
                  <span v-if="item._hasChildren" @click.stop="toggleExpand(item._id)" class="cs-toggle">
                    <svg :class="['cs-toggle-icon', { 'cs-toggle-open': expandedIds[item._id] }]" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg>
                  </span>
                  <span v-else class="cs-toggle-space" />
                  <div :class="['w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 mr-2.5 transition-colors', isBrowseSelected(item._id) ? 'bg-[var(--color-primary)] border-[var(--color-primary)]' : 'border-gray-300']">
                    <svg v-if="isBrowseSelected(item._id)" class="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="text-sm font-medium" :class="isBrowseSelected(item._id) ? 'text-[var(--color-primary)]' : 'text-gray-700'">{{ item.name }}</p>
                    <p v-if="item.description" class="text-sm text-gray-400 truncate">{{ item.description }}</p>
                  </div>
                </div>
              </div>
            </div>
            <!-- Footer -->
            <div class="px-6 py-4 border-t border-gray-100 flex items-center justify-between shrink-0">
              <span class="text-sm text-gray-400">{{ browseSelected.length }} selected</span>
              <div class="flex gap-3">
                <button type="button" @click="cancelBrowse" class="btn-secondary">Cancel</button>
                <button type="button" @click="applyBrowse" class="btn-primary">Done</button>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script>
import api from '../services/api.js'

export default {
  name: 'CategorySelect',
  props: {
    modelValue: { type: Array, default: () => [] },
    placeholder: { type: String, default: 'Select categories...' },
    disabled: { type: Boolean, default: false },
    preload: { type: Boolean, default: false },
    // Toolbar height (36px) instead of the form-field height.
    compact: { type: Boolean, default: false }
  },
  emits: ['update:modelValue'],

  data() {
    return {
      categories: [],
      loaded: false,
      loading: false,
      expandedIds: {},
      triggerWidth: 0,
      // Browse modal
      showBrowse: false,
      browseQuery: '',
      browseSelected: []
    }
  },

  computed: {
    flatList() {
      const result = []
      const walk = (items, depth, ancestors) => {
        for (const item of items) {
          const hasChildren = item.children?.length > 0
          result.push({ _id: item._id, name: item.name, description: item.description || '', _depth: depth, _hasChildren: hasChildren, _ancestors: ancestors })
          if (hasChildren) walk(item.children, depth + 1, [...ancestors, item])
        }
      }
      walk(this.categories, 0, [])
      return result
    },
    selectedCategories() {
      if (!this.loaded) return []
      const ids = this.modelValue || []
      return ids.map(id => {
        const cat = this.flatList.find(c => c._id === id || c._id?.toString() === id?.toString())
        return cat || { _id: id, name: id }
      }).filter(c => c.name !== c._id)
    },
    maxChips() {
      // Estimate: each chip ~100px, count badge ~40px, icon ~30px, padding ~20px
      const available = this.triggerWidth - 60 // icon + padding
      if (available <= 0) return 1
      const chipWidth = 110 // average chip + gap
      const countBadgeWidth = 45
      const total = this.selectedCategories.length
      if (total <= 1) return total
      const fitsWithCount = Math.floor((available - countBadgeWidth) / chipWidth)
      const fitsAll = Math.floor(available / chipWidth)
      // If all fit without count badge, show all
      if (fitsAll >= total) return total
      // Otherwise show as many as fit with the count badge
      return Math.max(1, fitsWithCount)
    },
    visibleChips() {
      return this.selectedCategories.slice(0, this.maxChips)
    },
    hiddenCount() {
      return Math.max(0, this.selectedCategories.length - this.maxChips)
    },
    browseVisibleItems() {
      let items = this.flatList
      if (this.browseQuery) {
        const q = this.browseQuery.toLowerCase()
        items = items.filter(item => item.name?.toLowerCase().includes(q))
      } else {
        const result = []
        for (const item of items) {
          let hidden = false
          for (const anc of item._ancestors) {
            if (!this.expandedIds[anc._id]) { hidden = true; break }
          }
          if (!hidden) result.push(item)
        }
        items = result
      }
      return items
    },
  },

  watch: {
    modelValue(val) {
      const hasValue = Array.isArray(val) && val.length > 0
      if (hasValue && !this.loaded) this.loadCategories()
    }
  },

  mounted() {
    const hasValue = Array.isArray(this.modelValue) && this.modelValue.length > 0
    if ((hasValue || this.preload) && !this.loaded) this.loadCategories()
    // Track trigger width for dynamic chip count
    this.$nextTick(() => {
      if (this.$refs.trigger) {
        this.triggerWidth = this.$refs.trigger.clientWidth
        this._resizeObserver = new ResizeObserver(entries => {
          this.triggerWidth = entries[0]?.contentRect?.width || 0
        })
        this._resizeObserver.observe(this.$refs.trigger)
      }
    })
  },

  beforeUnmount() {
    if (this._resizeObserver) this._resizeObserver.disconnect()
  },

  methods: {
    async loadCategories() {
      this.loading = true
      try {
        const res = await api.get('/categories', { params: { tree: true }, silent: true })
        this.categories = res.data?.items || []
        this.loaded = true
        const expanded = {}
        for (const item of this.flatList) {
          if (item._hasChildren) expanded[item._id] = true
        }
        this.expandedIds = expanded
      } catch { /* silent */ } finally { this.loading = false }
    },

    remove(id) {
      const ids = (this.modelValue || []).filter(v => v !== id && v?.toString() !== id?.toString())
      this.$emit('update:modelValue', ids)
    },

    toggleExpand(id) {
      this.expandedIds = { ...this.expandedIds, [id]: !this.expandedIds[id] }
    },

    // Browse modal
    async openBrowse() {
      if (!this.loaded) await this.loadCategories()
      this.browseSelected = [...(this.modelValue || [])]
      this.browseQuery = ''
      this.showBrowse = true
    },

    toggleBrowse(item) {
      const strId = item._id?.toString()
      const idx = this.browseSelected.findIndex(id => id === item._id || id?.toString() === strId)
      if (idx >= 0) {
        this.browseSelected.splice(idx, 1)
      } else {
        this.browseSelected.push(item._id)
      }
    },

    isBrowseSelected(id) {
      return this.browseSelected.some(v => v === id || v?.toString() === id?.toString())
    },

    applyBrowse() {
      this.$emit('update:modelValue', [...this.browseSelected])
      this.showBrowse = false
    },

    cancelBrowse() {
      this.showBrowse = false
    },

    getCategoryName(id) {
      if (!id) return ''
      const cat = this.flatList.find(c => c._id === id || c._id?.toString() === id?.toString())
      return cat?.name || ''
    }
  }
}
</script>

<style scoped>
.cs-root { position: relative; }
.cs-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 42px;
  padding: 6px 10px;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s;
}
.cs-trigger:hover { border-color: #d1d5db; }
.cs-trigger--compact { min-height: 36px; padding: 3px 10px; border-radius: 8px; }
.cs-trigger--compact .cs-chip { padding: 2px 6px 2px 8px; }
.cs-display { display: flex; align-items: center; gap: 6px; flex: 1; min-width: 0; }
.cs-chip { display: inline-flex; align-items: center; gap: 4px; padding: 3px 8px 3px 10px; border-radius: 6px; font-size: 12px; font-weight: 500; font-family: 'Poppins', sans-serif; background: var(--color-primary); color: #fff; white-space: nowrap; flex-shrink: 0; max-width: 200px; overflow: hidden; text-overflow: ellipsis; }
.cs-chip-x { display: flex; align-items: center; justify-content: center; width: 16px; height: 16px; border-radius: 4px; cursor: pointer; opacity: 0.7; transition: opacity 0.1s, background 0.1s; flex-shrink: 0; }
.cs-chip-x:hover { opacity: 1; background: rgba(255,255,255,0.2); }
.cs-count { font-size: 12px; font-weight: 600; color: var(--color-primary); background: var(--color-primary-light); padding: 2px 8px; border-radius: 10px; white-space: nowrap; flex-shrink: 0; }
.cs-placeholder { font-size: 13px; color: #c0c0c0; }
.cs-loading-chips { display: flex; align-items: center; gap: 6px; flex: 1; }
.cs-chip-skeleton { height: 24px; width: 80px; border-radius: 6px; background: #e5e7eb; }
.cs-chip-skeleton--sm { width: 60px; }
.cs-browse-icon { color: #9ca3af; flex-shrink: 0; transition: color 0.15s; }
.cs-trigger:hover .cs-browse-icon { color: var(--color-primary); }
/* Tree styles (used in modal) */
.cs-toggle { display: flex; align-items: center; justify-content: center; width: 20px; height: 20px; flex-shrink: 0; border-radius: 4px; cursor: pointer; transition: background 0.1s; }
.cs-toggle:hover { background: #e5e7eb; }
.cs-toggle-icon { color: #9ca3af; transition: transform 0.15s; }
.cs-toggle-open { transform: rotate(90deg); }
.cs-toggle-space { width: 20px; height: 20px; flex-shrink: 0; }
.cs-spinner { width: 18px; height: 18px; border: 2px solid #e5e7eb; border-top-color: var(--color-primary); border-radius: 50%; animation: cs-spin 0.6s linear infinite; }
@keyframes cs-spin { to { transform: rotate(360deg); } }
.cs-dd-enter-active { transition: opacity 0.12s ease; }
.cs-dd-leave-active { transition: opacity 0.08s ease; }
.cs-dd-enter-from, .cs-dd-leave-to { opacity: 0; }
</style>
